import { ArrowUp, ChevronDown, ChevronSort, ChevronUp } from '@carbon/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Link } from 'react-router';

import { Button } from 'DesignSystem/ui/button';
import { MetaProps, rightTableCellMeta } from 'DesignSystem/ui/table-meta-props';
import { cn } from 'DesignSystem/utils';
import LongText from 'components/LongText';
import type { DisruptionSortBy, DisruptionSortFields, PaginatedItemQueueItem } from 'pages/disruption/types';
import { formatNumber } from 'utils/helpers';
import { getManipulatedERPId } from 'utils/tmpDataManipulation';

import { DohCell, QohCell } from './event-items-inventory-cells';

type EventItemsColumnsArgs = {
  sort: DisruptionSortBy;
  onSort: (field: DisruptionSortFields) => void;
  expandedIds: Record<string, boolean>;
  onToggleExpanded: (id: string) => void;
};

const descriptionTableCellMeta: MetaProps = {
  tableCellProps: {
    className: 'max-w-[230px] overflow-hidden',
    style: { maxWidth: 230 },
  },
};

const columnHelper = createColumnHelper<PaginatedItemQueueItem>();

export function getEventItemsColumns({ sort, onSort, expandedIds, onToggleExpanded }: EventItemsColumnsArgs) {
  return [
    columnHelper.accessor('client_erp_item_id', {
      id: 'client_erp_item_id',
      header: () => (
        <EventItemsSortHeader label="ERP Item ID" sortField="client_erp_item_id" sort={sort} onSort={onSort} />
      ),
      cell: ({ row }) => <ErpItemIdCell item={row.original} />,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      meta: descriptionTableCellMeta,
      header: () => <EventItemsSortHeader label="Description" sortField="description" sort={sort} onSort={onSort} />,
      cell: ({ row }) => <EventItemDescriptionCell text={row.original.description} />,
    }),
    columnHelper.accessor('client_manufacturer_name', {
      id: 'client_manufacturer_name',
      header: () => (
        <EventItemsSortHeader label="MFG Name" sortField="client_manufacturer_name" sort={sort} onSort={onSort} />
      ),
      cell: ({ row }) => {
        const text = row.original.client_manufacturer_name;
        return text ? <LongText text={text} /> : '–';
      },
    }),
    columnHelper.accessor('unit_quantity_on_hand', {
      id: 'unit_quantity_on_hand',
      meta: rightTableCellMeta,
      header: () => (
        <EventItemsSortHeader label="QOH" sortField="unit_quantity_on_hand" sort={sort} onSort={onSort} isNumeric />
      ),
      cell: ({ row }) => <QohCell value={row.original.unit_quantity_on_hand} />,
    }),
    columnHelper.accessor('unit_days_on_hand', {
      id: 'unit_days_on_hand',
      meta: rightTableCellMeta,
      header: () => (
        <EventItemsSortHeader label="DOH" sortField="unit_days_on_hand" sort={sort} onSort={onSort} isNumeric />
      ),
      cell: ({ row }) => <DohCell value={row.original.unit_days_on_hand} />,
    }),
    columnHelper.accessor('unit_average_daily_usage', {
      id: 'unit_average_daily_usage',
      meta: rightTableCellMeta,
      header: () => (
        <EventItemsSortHeader
          label="Avg. Daily Use"
          sortField="unit_average_daily_usage"
          sort={sort}
          onSort={onSort}
          isNumeric
        />
      ),
      cell: ({ row }) => {
        const adu = row.original.unit_average_daily_usage;
        return typeof adu === 'number' ? formatNumber(adu, 2) : '–';
      },
    }),
    columnHelper.accessor('open_purchase_order_count', {
      id: 'open_purchase_order_count',
      meta: rightTableCellMeta,
      header: () => (
        <EventItemsSortHeader
          label="Open POs"
          sortField="open_purchase_order_count"
          sort={sort}
          onSort={onSort}
          isNumeric
        />
      ),
      cell: ({ row }) => {
        const count = row.original.open_purchase_order_count;
        return typeof count === 'number' ? formatNumber(count) : '–';
      },
    }),
    columnHelper.accessor('open_purchase_order_unit_quantity', {
      id: 'open_purchase_order_unit_quantity',
      meta: rightTableCellMeta,
      header: () => (
        <EventItemsSortHeader
          label="On Order Qty"
          sortField="open_purchase_order_unit_quantity"
          sort={sort}
          onSort={onSort}
          isNumeric
        />
      ),
      cell: ({ row }) => {
        const qty = row.original.open_purchase_order_unit_quantity;
        return typeof qty === 'number' ? formatNumber(Math.round(qty)) : '–';
      },
    }),
    columnHelper.accessor('approved_substitution_count', {
      id: 'approved_substitution_count',
      meta: rightTableCellMeta,
      header: () => (
        <EventItemsSortHeader
          label="Approved Substitutes"
          sortField="approved_substitution_count"
          sort={sort}
          onSort={onSort}
          isNumeric
        />
      ),
      cell: ({ row }) => (
        <ApprovedSubsCell
          item={row.original}
          isExpanded={Boolean(expandedIds[row.original.client_item_master_id])}
          onToggle={() => onToggleExpanded(row.original.client_item_master_id)}
        />
      ),
    }),
  ];
}

function EventItemDescriptionCell({ text }: { text: string | null | undefined }) {
  return <div className="max-w-[230px] overflow-hidden">{text ? <LongText text={text} /> : '–'}</div>;
}

function EventItemsSortHeader({
  label,
  sortField,
  sort,
  onSort,
  isNumeric,
}: {
  label: string;
  sortField: DisruptionSortFields;
  sort: DisruptionSortBy;
  onSort: (field: DisruptionSortFields) => void;
  isNumeric?: boolean;
}) {
  const [field, dir] = sort.split('|');
  const isActive = field === sortField;
  const isDesc = dir === 'desc';

  return (
    <button
      type="button"
      onClick={() => onSort(sortField)}
      className={cn(
        'group flex h-full items-center gap-2 whitespace-nowrap align-text-bottom',
        isNumeric && 'justify-end'
      )}
    >
      {isNumeric ? <SortIcon isActive={isActive} isDesc={isDesc} /> : null}
      {label}
      {!isNumeric ? <SortIcon isActive={isActive} isDesc={isDesc} /> : null}
    </button>
  );
}

function SortIcon({ isActive, isDesc }: { isActive: boolean; isDesc: boolean }) {
  if (isActive) {
    return (
      <div className="flex size-4 items-center justify-center">
        <ArrowUp className={cn('size-3', isDesc && 'rotate-180')} />
      </div>
    );
  }
  return <ChevronSort className="size-4 opacity-0 group-hover:opacity-70" />;
}

function ErpItemIdCell({ item }: { item: PaginatedItemQueueItem }) {
  const flags = useFlags();
  const erpId = flags['demoDataManipulation']
    ? getManipulatedERPId(item.client_erp_item_id || '')
    : item.client_erp_item_id;

  if (!item.client_erp_item_id) return <>–</>;

  return (
    <Link to={`/disruptions/${item.client_item_master_id}`} className="font-medium text-indigo-500 hover:underline">
      {erpId}
    </Link>
  );
}

function ApprovedSubsCell({
  item,
  isExpanded,
  onToggle,
}: {
  item: PaginatedItemQueueItem;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const count = item.approved_substitution_count;

  if (!count) {
    return <>–</>;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 gap-1 rounded-full px-2.5"
      aria-expanded={isExpanded}
      aria-label={
        isExpanded
          ? `Collapse approved substitutes for ${item.client_erp_item_id ?? 'item'}`
          : `Expand approved substitutes for ${item.client_erp_item_id ?? 'item'}`
      }
      onClick={onToggle}
    >
      {count}
      {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
    </Button>
  );
}
